export declare type Listener = (...args: any) => void;
export declare type DefaultEventMap = { [event in string | symbol]: Listener };

type GetEventDataMap<T extends DefaultEventMap> = {
  [K in keyof T]: { event: K; data: Parameters<T[K]> };
}[keyof T];

/**
 * Помогает создавать подписки на различные события/изменения с помощью методов on/off
 */
export class EventEmitter<EventMap extends DefaultEventMap = DefaultEventMap> {
  private eventListeners: { [eventName in keyof EventMap]?: Listener[] } = {};
  private subscribes: Array<(event: GetEventDataMap<EventMap>) => void> = [];

  /**
   * Файрит событие
   * @param event событие
   * @param data параметр
   */
  public emit = <EventKey extends keyof EventMap>(event: EventKey, ...data: Parameters<EventMap[EventKey]>) => {
    const eventListeners: Listener[] | undefined = this.eventListeners[event];

    if (eventListeners) {
      eventListeners.forEach((listener) => {
        listener(...data);
      });
    }

    this.subscribes.forEach((subscriber) => {
      subscriber({ event, data });
    });
  };

  /**
   * Добавляет подписку на одно событие
   * @param event событие
   * @param listener подписчик
   */
  public on = <EventKey extends keyof EventMap>(event: EventKey, listener: EventMap[EventKey]): this => {
    this.eventListeners[event] = this.eventListeners[event] || [];
    const eventListeners = this.eventListeners[event] as Listener[];

    if (!eventListeners.includes(listener)) {
      eventListeners.push(listener);
    }

    return this;
  };

  /**
   * Удаляет подписку с одного события
   * @param event событие
   * @param listener установленный ранее подписчик. Если не указан, удаляет все подписчики данного события
   */
  public off = <EventKey extends keyof EventMap = string>(event: EventKey, listener?: EventMap[EventKey]): this => {
    this.eventListeners[event] = this.eventListeners[event] || [];
    const eventListeners = this.eventListeners[event] as Listener[];

    this.eventListeners[event] = listener ? eventListeners.filter((eventListener) => eventListener !== listener) : [];

    if (!this.eventListeners[event].length) {
      delete this.eventListeners[event];
    }

    return this;
  };

  /**
   * Добавляет подписку на любое событие
   * @param subscriber подписчик
   */
  public subscribe = (subscriber: (event: GetEventDataMap<EventMap>) => void): this => {
    if (!this.subscribes.includes(subscriber)) {
      this.subscribes.push(subscriber);
    }

    return this;
  };

  /**
   * Удаляет подписку с любого события
   * @param subscriber подписчик
   */
  public unsubscribe = (subscriber: (event: GetEventDataMap<EventMap>) => void): this => {
    this.subscribes = this.subscribes.filter((subscribeItem) => subscribeItem !== subscriber);

    return this;
  };

  public reset = () => {
    this.eventListeners = {};
    this.subscribes = [];
  };
}
